import { ColumnsType } from 'antd/lib/table/interface';
import { TextCell } from 'app/components/Table';
import { formatDate } from 'app/utils';
import { StepTemplateInformation } from './components';
import LinkageProduct from './components/Steps/LinkageProduct';
import styled from '@emotion/styled';
import { Space, Tag } from 'antd';
import { ReactComponent as DuplicateIcon } from 'assets/icons/Duplicate.svg';
import { ReactComponent as EditIcon } from 'assets/icons/Edit.svg';
import { ReactComponent as InactiveIcon } from 'assets/icons/Inactive.svg';
import { ReactComponent as DefaultIcon } from 'assets/icons/start-icon.svg';
import DropdownMenuCell from 'app/components/Table/Cells/DropdownMenuCell';

const StatusText = styled(TextCell, {
  shouldForwardProp: (prop) => prop !== '$isActive'
})<{ $isActive: boolean }>`
  color: ${(props) => (props.$isActive ? '#1EC000' : '#ED4646')};
`;

const StatusDot = styled('div', {
  shouldForwardProp: (prop) => prop !== '$isActive'
})<{ $isActive: boolean }>`
  background-color: ${(props) => (props.$isActive ? '#1EC000' : '#ED4646')};
  width: 10px;
  height: 10px;
  border-radius: 10px;
`;

const DefaultTemplateTag = styled(Tag)`
  border-radius: 13px;
  border: none;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.005em;
  text-align: left;
`;

export const getColumnDefs = (
  onDetail: (id: number) => void,
  onDuplicate: (id: number) => void,
  activeTemplate: (id: string) => void,
  inActiveTemplate: (id: string) => void,
  setDefault: (id: string) => void
): ColumnsType<any> => {
  return [
    {
      title: 'Template No.',
      dataIndex: 'templateNo',
      key: 'templateNo',
      render: (value) => {
        return <TextCell>{value}</TextCell>;
      }
    },
    {
      title: 'Template Name',
      dataIndex: 'name',
      key: 'name',
      // sorter: true,
      // ...getColumnSearchProps('name', 'Search template name'),
      render: (value, record) => {
        return (
          <TextCell>
            {value}{' '}
            <DefaultTemplateTag hidden={!record.isDefault} color="success">
              Default Template
            </DefaultTemplateTag>
          </TextCell>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'status',
      render: (value) => {
        return (
          <Space>
            <StatusDot $isActive={value} />
            <StatusText $isActive={value}>
              {value ? 'Active' : 'Inactive'}
            </StatusText>
          </Space>
        );
      }
    },
    {
      title: 'Create Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      // sorter: true,
      render: (value) => {
        return <TextCell>{formatDate(value, 'DD MMM YYYY')}</TextCell>;
      }
    },
    {
      title: 'Last Update Date',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      // sorter: true,
      render: (value) => {
        return <TextCell>{formatDate(value, 'DD MMM YYYY')}</TextCell>;
      }
    },
    {
      title: '',
      render: (_, record) => {
        return (
          <DropdownMenuCell
            placements={['bottomRight']}
            options={[
              {
                key: 'duplicate',
                label: 'Duplicate Template',
                icon: <DuplicateIcon />,
                onClick: () => {
                  onDuplicate(record.id);
                }
              },
              {
                key: 'edit',
                label: 'Edit Template',
                icon: <EditIcon />,
                onClick: () => {
                  onDetail(record.id);
                }
              },
              {
                key: 'active',
                label: record.isActive
                  ? 'Inactive Template'
                  : 'Active Template',
                icon: <InactiveIcon />,
                isApplicable: () => !record.isDefault,
                onClick: () => {
                  record.isActive
                    ? inActiveTemplate(record.id)
                    : activeTemplate(record.id);
                }
              },
              {
                key: 'default',
                label: 'Set As Default Template',
                icon: <DefaultIcon />,
                isApplicable: () => !record.isDefault && record.isActive,
                onClick: () => {
                  setDefault(record.id);
                }
              }
            ]}
          />
        );
      }
    }
  ];
};

export const TemplateInformationSteps = [
  {
    key: 'template-information',
    title: 'Template Information',
    content: StepTemplateInformation
  },
  {
    key: 'linkage-produce',
    title: 'Product Linkage',
    content: LinkageProduct
  }
];

export const OrderSections = {
  quantityAvailable: '1',
  productDescriptionStyleColor: '2',
  workmanship: '3',
  onSiteTest: '4',
  packingMarking: '5',
  importantRemarksAttachments: '6'
};

export const AdminTemplateConfigs = [
  {
    fieldName: 'requestProduced',
    name: 'defaultValue',
    type: 'INPUT',
    inputProps: {
      suffix: '%'
    }
  },
  {
    fieldName: 'requestPacked',
    name: 'defaultValue',
    type: 'INPUT',
    inputProps: {
      suffix: '%'
    }
  },
  //Product Description Style And Color
  {
    fieldName: 'appearance',
    name: 'required',
    type: 'CHECKBOX',
    label: 'Required'
  },
  {
    fieldName: 'accessories',
    name: 'required',
    type: 'CHECKBOX',
    label: 'Required'
  },
  {
    fieldName: 'material',
    name: 'required',
    type: 'CHECKBOX',
    label: 'Required'
  },
  {
    fieldName: 'package',
    name: 'required',
    type: 'CHECKBOX',
    label: 'Required'
  },
  {
    fieldName: 'labelling',
    name: 'required',
    type: 'CHECKBOX',
    label: 'Required'
  },
  //Common
  {
    fieldName: 'referenceMedia',
    name: 'required',
    type: 'CHECKBOX',
    label: 'Required'
  },
  {
    fieldName: 'attachments',
    name: 'required',
    type: 'CHECKBOX',
    label: 'Required'
  },
  {
    fieldName: 'sectionResult',
    name: 'required',
    type: 'CHECKBOX',
    label: 'Required'
  },
  {
    fieldName: 'sectionRemark',
    name: 'required',
    type: 'CHECKBOX',
    label: 'Required'
  },
  // DefectList
  {
    fieldName: 'defectList',
    type: 'DEFECT',
    name: 'value'
  },
  {
    fieldName: 'defectName',
    type: 'INPUT',
    name: 'defaultValue'
  },
  {
    fieldName: 'defectGroup',
    type: 'SELECT',
    name: 'defaultValue'
  },
  {
    fieldName: 'defectGroup',
    type: 'CHECKBOX',
    name: 'defaultValue'
  }
];
export const LayoutConfigs = {
  quantityAvailable: [1, [2, 3], [4, 5], [6, 7], 8, [9, 10]],
  productDescriptionStyleColor: [1, [2, 3], [4, 5], 6, 7, [8, 9]],
  workmanship: [1, 2, 3],
  packingMarking: [1, 2, 3],
  importantRemarksAttachments: [1, 2],
  sellingUnitContent: [[1, 2, 3], 4, [5, 6], [7, 8], [9, 10], 11, [12, 13]],
  innerPackContent: [[1, 2, 3], 4, [5, 6], [7, 8], [9, 10], 11, 12, [13, 14]],
  outerCartonContent: [
    [1, 2, 3],
    4,
    [5, 6],
    [7, 8],
    [9, 10],
    [11, 12],
    13,
    [14, 15]
  ],
  onSiteTest: [1]
};
export const TemplateConfig = [
  {
    name: 'quantityAvailable',
    value: 'Quantity Available',
    enabled: true,
    fields: [
      {
        name: 'bookQuantity',
        label: 'Book QTY',
        type: 'INPUT',
        enabled: true,
        required: true,
        description: 'Quantity from Inspection Job',
        order: 1
      },
      {
        name: 'requestProduced',
        label: 'Request Produced %',
        type: 'INPUT',
        dataType: 'NUMBER',
        formatType: 'PERCENTAGE',
        defaultValue: '',
        suffix: '%',
        min: 0,
        enabled: true,
        optional: true,
        required: true,
        order: 2
      },
      {
        name: 'requestPacked',
        label: 'Request Packed %',
        type: 'INPUT',
        dataType: 'NUMBER',
        formatType: 'PERCENTAGE',
        defaultValue: '',
        suffix: '%',
        max: 0,
        enabled: true,
        optional: true,
        required: true,
        order: 3
      },
      {
        name: 'producedQuantity',
        label: 'Produced Qty',
        type: 'INPUT',
        dataType: 'NUMBER',
        formatType: 'PERCENTAGE',
        defaultValue: '',
        suffix: '%',
        min: 0,
        enabled: true,
        optional: true,
        required: true,
        order: 4,
        description: 'Input by inspector'
      },
      {
        name: 'producedPercentage',
        label: 'Produced %',
        type: 'INPUT',
        dataType: 'NUMBER',
        formatType: 'PERCENTAGE',
        defaultValue: '',
        suffix: '%',
        max: 0,
        enabled: true,
        optional: true,
        required: true,
        order: 5,
        description: 'Auto calculate - (produced qty / booked qty) x 100%'
      },
      {
        name: 'packedQuantity',
        label: 'Packed Qty',
        type: 'INPUT',
        dataType: 'NUMBER',
        formatType: 'PERCENTAGE',
        defaultValue: '',
        suffix: '%',
        min: 0,
        enabled: true,
        required: true,
        description: 'Input by inspector',
        order: 6
      },
      {
        name: 'packedPercentage',
        label: 'Packed %',
        type: 'INPUT',
        dataType: 'NUMBER',
        formatType: 'PERCENTAGE',
        defaultValue: '',
        suffix: '%',
        max: 0,
        enabled: true,
        required: true,
        order: 7,
        description: 'Auto calculate - (packed qty / booked qty) x 100%'
      },
      {
        name: 'result',
        label: 'Section Result',
        type: 'CHECKBOX',
        options: [
          {
            value: 'Conformed',
            label: 'conformed'
          },
          {
            value: 'Not Conformed',
            label: 'not_conformed'
          },
          {
            value: 'Pending',
            label: 'pending'
          }
        ],
        required: false,
        description: 'Conformed / not conformed / pending by inspector',
        order: 8
      },
      {
        name: 'remark',
        label: 'Section Remarks',
        placeholder: '',
        type: 'TEXTAREA',
        description: 'Free Text',
        mandatory: false,
        required: false,
        optional: false,
        order: 9
      },
      {
        name: 'referenceMedia',
        label: 'Reference Media',
        description: 'Support multiple files upload',
        placeholder: '',
        type: 'UPLOAD',
        multiple: true,
        required: false,
        order: 10
      }
    ],
    configs: [
      {
        fieldName: 'requestProduced',
        name: 'defaultValue',
        type: 'INPUT',
        inputProps: {
          suffix: '%'
        }
      },
      {
        fieldName: 'requestPacked',
        name: 'defaultValue',
        type: 'INPUT',
        inputProps: {
          suffix: '%'
        }
      },
      {
        fieldName: 'result',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      },
      {
        fieldName: 'remark',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      },
      {
        fieldName: 'referenceMedia',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      }
    ]
  },
  {
    name: 'productDescriptionStyleColor',
    value: 'Product Description, Style & Color',
    enabled: true,
    fields: [
      {
        name: 'signedSampleAvailable',
        label: 'Signed Sample available?',
        type: 'INPUT',
        enabled: true,
        required: true,
        description: 'QA - Marked as “Yes”',
        order: 1
      },
      {
        name: 'appearance',
        label: 'Appearance',
        type: 'INPUT',
        enabled: true,
        required: true,
        description: 'Conformed or not conform by inspector',
        order: 2
      },
      {
        name: 'accessories',
        label: 'Accessories',
        type: 'INPUT',
        enabled: true,
        required: true,
        description: 'Conformed or not conform by inspector',
        order: 3
      },
      {
        name: 'material',
        label: 'Material',
        type: 'INPUT',
        enabled: true,
        required: true,
        description: 'Conformed or not conform by inspector',
        order: 4
      },
      {
        name: 'package',
        label: 'Package',
        type: 'INPUT',
        enabled: true,
        required: true,
        description: 'Conformed or not conform by inspector',
        order: 5
      },
      {
        name: 'labelling',
        label: 'Labelling',
        type: 'INPUT',
        enabled: true,
        required: true,
        description: 'Conformed or not conform by inspector',
        order: 6
      },
      {
        name: 'result',
        label: 'Section Result',
        type: 'CHECKBOX',
        options: [
          {
            value: 'Conformed',
            label: 'conformed'
          },
          {
            value: 'Not Conformed',
            label: 'not_conformed'
          },
          {
            value: 'Pending',
            label: 'pending'
          }
        ],
        required: false,
        description: 'Conformed / not conformed / pending by inspector',
        order: 7
      },
      {
        name: 'remark',
        label: 'Section Remarks',
        placeholder: '',
        type: 'TEXTAREA',
        description: 'Free Text',
        mandatory: false,
        required: false,
        optional: false,
        order: 8
      },
      {
        name: 'referenceMedia',
        label: 'Reference Media',
        description: 'Support multiple files upload',
        placeholder: '',
        type: 'UPLOAD',
        multiple: true,
        required: false,
        order: 9
      }
    ],
    configs: [
      {
        fieldName: 'appearance',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      },
      {
        fieldName: 'accessories',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      },
      {
        fieldName: 'material',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      },
      {
        fieldName: 'package',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      },
      {
        fieldName: 'labelling',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      },

      {
        fieldName: 'result',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      },
      {
        fieldName: 'remark',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      },
      {
        fieldName: 'referenceMedia',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      }
    ]
  },
  {
    name: 'workmanship',
    value: 'Workmanship',
    enabled: true,
    fields: [
      {
        name: 'defects',
        label: 'Defect list',
        type: 'LIST',
        defaultValue: [],
        value: [],
        items: [],
        order: 1
      },
      {
        name: 'remark',
        label: 'Section Remarks',
        placeholder: '',
        type: 'TEXTAREA',
        description: 'Free Text',
        mandatory: false,
        required: false,
        optional: false,
        order: 2
      },
      {
        name: 'numberOfDefect',
        label: 'Number of Defect',
        placeholder: '',
        type: 'INPUT',
        description: 'Sum of defects found in this section',
        required: false,
        order: 3
      }
    ],
    configs: [
      {
        fieldName: 'defects',
        name: 'items',
        type: 'DEFECTS',
        itemFields: [
          {
            name: 'defectName',
            label: 'Defect Name',
            type: 'LABEL',
            value: '',
            order: 1
          },
          {
            name: 'defectGroup',
            label: 'Defect Group',
            defaultValue: '',
            value: '',
            order: 2
          },
          {
            name: 'defectClassification',
            label: 'Defect Classification',
            type: 'LABEL',
            value: 'critical',
            order: 3
          },
          {
            name: 'referenceMedia',
            label: 'Reference Media',
            placeholder: '',
            type: 'UPLOAD',
            multiple: true,
            required: false,
            order: 4
          },
          {
            name: 'quantity',
            label: 'Quantity',
            description: 'Number of individual defect found(Inspector input)',
            placeholder: '',
            type: 'INPUT',
            multiple: true,
            required: false,
            order: 5
          }
        ],
        configs: [
          {
            fieldName: 'defectName',
            name: 'value',
            type: 'INPUT',
            inputProps: {}
          },
          {
            fieldName: 'defectGroup',
            name: 'value',
            type: 'SELECT',
            inputProps: {},
            options: [
              {
                value: 'Functionality',
                label: 'Functionality'
              },
              {
                value: 'Instruction Manual',
                label: 'Instruction Manual'
              },
              {
                value: 'Packing & Shipping Info',
                label: 'Packing & Shipping Info'
              },
              {
                value: 'Product Specification',
                label: 'Product Specification'
              },
              {
                value: 'Safety',
                label: 'Safety'
              },
              {
                value: 'Workmanship',
                label: 'Workmanship'
              },
              {
                value: 'Others',
                label: 'Others'
              }
            ]
          },
          {
            fieldName: 'defectClassification',
            name: 'value',
            type: 'RADIO',
            options: [
              {
                value: 'critical',
                label: 'Critical'
              },
              {
                value: 'major',
                label: 'Major'
              },
              {
                value: 'minor',
                label: 'Minor'
              }
            ]
          },
          {
            fieldName: 'referenceMedia',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          }
        ]
      },
      {
        fieldName: 'remark',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      }
    ]
  },
  {
    name: 'packingMarking',
    value: 'Packing & Marking',
    enabled: true,
    configs: [
      {
        fieldName: 'sellingUnitContent',
        name: 'fields',
        type: 'SECTION',
        configs: [
          {
            fieldName: 'netWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'actualNetWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'grossWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'actualGrossWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'result',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'remark',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'referenceMedia',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          }
        ]
      },
      {
        fieldName: 'innerPackContent',
        name: 'fields',
        type: 'SECTION',
        configs: [
          {
            fieldName: 'netWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'actualNetWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'grossWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'actualGrossWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'result',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'remark',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'referenceMedia',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          }
        ]
      },
      {
        fieldName: 'outerCartonContent',
        name: 'fields',
        type: 'SECTION',
        configs: [
          {
            fieldName: 'netWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'actualNetWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'grossWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'actualGrossWeight',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'result',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'remark',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'referenceMedia',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          }
        ]
      }
    ],
    fields: [
      {
        name: 'sellingUnitContent',
        type: 'SECTION',
        label: 'Selling Unit Content',
        required: false,
        order: 1,
        fields: [
          {
            name: 'length',
            label: 'Length(L)',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 1
          },
          {
            name: 'width',
            label: 'Width(W)',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 2
          },
          {
            name: 'height',
            label: 'Height(H)',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 3
          },
          {
            name: 'dim',
            label: 'Dim. Var.',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 4
          },
          {
            name: 'barcode',
            label: 'Barcode',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 5
          },
          {
            name: 'actualBarcode',
            label: 'Actual Barcode',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 6
          },
          {
            name: 'netWeight',
            label: 'Net Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 7
          },
          {
            name: 'actualNetWeight',
            label: 'Actual Net Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 8
          },
          {
            name: 'grossWeight',
            label: 'Gross Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 7
          },
          {
            name: 'actualGrossWeight',
            label: 'Actual Gross Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 8
          },
          {
            name: 'result',
            label: 'Section Result',
            type: 'CHECKBOX',
            options: [
              {
                value: 'Conformed',
                label: 'conformed'
              },
              {
                value: 'Not Conformed',
                label: 'not_conformed'
              },
              {
                value: 'Pending',
                label: 'pending'
              }
            ],
            required: false,
            description: 'Conformed / not conformed / pending by inspector',
            order: 9
          },
          {
            name: 'remark',
            label: 'Section Remarks',
            placeholder: '',
            type: 'TEXTAREA',
            description: 'Free Text',
            mandatory: false,
            required: false,
            optional: false,
            order: 10
          },
          {
            name: 'referenceMedia',
            label: 'Reference Media',
            placeholder: '',
            type: 'UPLOAD',
            multiple: true,
            required: false,
            order: 11
          }
        ],
        configs: [
          {
            fieldName: 'netWeight',
            name: 'required',
            type: 'CHECKBOX'
          },
          {
            fieldName: 'actualNetWeight',
            name: 'required',
            type: 'CHECKBOX'
          },
          {
            fieldName: 'grossWeight',
            name: 'required',
            type: 'CHECKBOX'
          },
          {
            fieldName: 'actualGrossWeight',
            name: 'required',
            type: 'CHECKBOX'
          },
          {
            fieldName: 'result',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'remark',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'referenceMedia',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          }
        ]
      },
      {
        name: 'innerPackContent',
        label: 'Inner Pack Content',
        type: 'SECTION',
        required: false,
        order: 2,
        fields: [
          {
            name: 'length',
            label: 'Length(L)',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 1
          },
          {
            name: 'width',
            label: 'Width(W)',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 2
          },
          {
            name: 'height',
            label: 'Height(H)',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 3
          },
          {
            name: 'dim',
            label: 'Dim. Var.',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 4
          },
          {
            name: 'barcode',
            label: 'Barcode',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 5
          },
          {
            name: 'actualBarcode',
            label: 'Actual Barcode',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 6
          },
          {
            name: 'netWeight',
            label: 'Net Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 7
          },
          {
            name: 'actualNetWeight',
            label: 'Actual Net Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 8
          },
          {
            name: 'grossWeight',
            label: 'Gross Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 7
          },
          {
            name: 'actualGrossWeight',
            label: 'Actual Gross Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 8
          },
          {
            name: 'innerQuantity',
            label: 'Inner Qty',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 9
          },
          {
            name: 'result',
            label: 'Section Result',
            type: 'CHECKBOX',
            options: [
              {
                value: 'Conformed',
                label: 'conformed'
              },
              {
                value: 'Not Conformed',
                label: 'not_conformed'
              },
              {
                value: 'Pending',
                label: 'pending'
              }
            ],
            required: false,
            description: 'Conformed / not conformed / pending by inspector',
            order: 10
          },
          {
            name: 'remark',
            label: 'Section Remarks',
            placeholder: '',
            type: 'TEXTAREA',
            description: 'Free Text',
            required: false,
            order: 11
          },
          {
            name: 'referenceMedia',
            label: 'Reference Media',
            description: 'Support multiple files upload',
            placeholder: '',
            type: 'UPLOAD',
            multiple: true,
            required: false,
            order: 12
          }
        ]
      },
      {
        name: 'outerCartonContent',
        label: 'Outer Carton Content',
        type: 'SECTION',
        required: false,
        order: 3,
        fields: [
          {
            name: 'length',
            label: 'Length(L)',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 1
          },
          {
            name: 'width',
            label: 'Width(W)',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 2
          },
          {
            name: 'height',
            label: 'Height(H)',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 3
          },
          {
            name: 'dim',
            label: 'Dim. Var.',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 4
          },
          {
            name: 'barcode',
            label: 'Barcode',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 5
          },
          {
            name: 'actualBarcode',
            label: 'Actual Barcode',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 6
          },
          {
            name: 'netWeight',
            label: 'Net Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 7
          },
          {
            name: 'actualNetWeight',
            label: 'Actual Net Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 8
          },
          {
            name: 'grossWeight',
            label: 'Gross Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 7
          },
          {
            name: 'actualGrossWeight',
            label: 'Actual Gross Weight ',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 8
          },
          {
            name: 'outerQuantity',
            label: 'Outer Qty',
            type: 'INPUT',
            enabled: true,
            required: true,
            description: 'Quantity from Inspection Job',
            order: 9
          },
          {
            name: 'result',
            label: 'Section Result',
            type: 'CHECKBOX',
            options: [
              {
                value: 'Conformed',
                label: 'conformed'
              },
              {
                value: 'Not Conformed',
                label: 'not_conformed'
              },
              {
                value: 'Pending',
                label: 'pending'
              }
            ],
            required: false,
            description: 'Conformed / not conformed / pending by inspector',
            order: 10
          },
          {
            name: 'remark',
            label: 'Section Remarks',
            placeholder: '',
            type: 'TEXTAREA',
            description: 'Free Text',
            mandatory: false,
            required: false,
            optional: false,
            order: 11
          },
          {
            name: 'referenceMedia',
            label: 'Reference Media',
            description: 'Support multiple files upload',
            placeholder: '',
            type: 'UPLOAD',
            multiple: true,
            required: false,
            order: 12
          }
        ]
      }
    ]
  },
  {
    name: 'onSiteTest',
    value: 'On-site Checking',
    enabled: true,
    fields: [
      {
        name: 'checkList',
        label: 'Content',
        type: 'LIST',
        defaultValue: [],
        value: [],
        items: [],
        order: 1
      }
    ],
    configs: [
      {
        fieldName: 'checkList',
        name: 'items',
        type: 'CHECKLIST',
        itemFields: [
          {
            name: 'checkPointName',
            label: 'Check Point Name',
            type: 'LABEL',
            value: '',
            order: 1
          },
          {
            name: 'checkCriteria',
            label: 'Check Criteria',
            defaultValue: '',
            value: '',
            order: 2
          },
          {
            name: 'result',
            label: 'Section Result',
            type: 'CHECKBOX',
            options: [
              {
                value: 'Conformed',
                label: 'conformed'
              },
              {
                value: 'Not Conformed',
                label: 'not_conformed'
              },
              {
                value: 'Pending',
                label: 'pending'
              }
            ],
            required: false,
            description: 'Conformed / not conformed / pending by inspector',
            order: 8
          },
          {
            name: 'remark',
            label: 'Section Remarks',
            placeholder: '',
            type: 'TEXTAREA',
            description: 'Free Text',
            mandatory: false,
            required: false,
            optional: false,
            order: 9
          },
          {
            name: 'referenceMedia',
            label: 'Reference Media',
            description: 'Support multiple files upload',
            placeholder: '',
            type: 'UPLOAD',
            multiple: true,
            required: false,
            order: 10
          }
        ],
        configs: [
          {
            fieldName: 'checkPointName',
            name: 'value',
            type: 'INPUT',
            inputProps: {
              style: {
                width: '100%'
              }
            }
          },
          {
            fieldName: 'checkCriteria',
            name: 'value',
            type: 'TEXTAREA',
            inputProps: {}
          },
          {
            fieldName: 'result',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'remark',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          },
          {
            fieldName: 'referenceMedia',
            name: 'required',
            type: 'CHECKBOX',
            label: 'Required'
          }
        ]
      },
      {
        fieldName: 'remark',
        name: 'required',
        type: 'CHECKBOX',
        label: 'Required'
      }
    ]
  },
  {
    name: 'importantRemarksAttachments',
    value: 'Important Remarks / Attachments',
    enabled: true,
    fields: [
      {
        name: 'remark',
        label: 'Important Remarks',
        placeholder: '',
        type: 'TEXTAREA',
        description:
          'mark as pending if pending further action / clarification from admin office',
        mandatory: false,
        required: false,
        optional: false,
        order: 1,
        adminConfig: []
      },
      {
        name: 'attachments',
        label: 'Attachments',
        placeholder: '',
        type: 'UPLOAD',
        multiple: true,
        required: false,
        order: 2,
        adminConfig: []
      }
    ]
  }
];
